import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Download,
  ExternalLink,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { DecimalAlignedMoney } from './DecimalAlignedMoney';
import {
  CASH_LEDGER_TABLE_HEADER_CLASS,
  CASH_LEDGER_TABLE_ROW_CLASS,
  CashLedgerTableViewport,
} from './CashLedgerTableLayout';

type Dataset = 'activity' | 'settlements';
type SortDirection = 'asc' | 'desc';
type CashSignBasis = 'comparison_fixture' | 'public_chain_fixture' | 'chain_exact' | 'unresolved';

interface TraderOption {
  id: string;
  username: string;
  role: string;
}

interface ActivityRow {
  sourceId: string;
  tsUtc: string;
  week: string;
  type: string;
  side: string;
  title: string;
  conditionId: string;
  outcome: string;
  shares: string;
  price: string;
  usdc: string;
  cashDelta: string;
  tx: string;
  trader: string;
  userId: string;
  attributionStatus: 'attributed' | 'unassigned' | 'ambiguous' | 'org_level';
  attributionReason: string;
  g1ExecutionRole: '' | 'maker' | 'taker';
  g1FeeUsdActual: string;
  g1EstimatedRebateUsd: string;
  chainMatchStatus: 'matched' | 'not_available' | 'ambiguous' | 'conflict';
  evidenceStatus: string;
  cashSignBasis: CashSignBasis;
  cashSignReason: string;
}

interface ActivitySnapshot {
  mode: 'historical-comparison';
  wallet: string;
  summary: {
    walletRows: number;
    returnedRows: number;
    excludedRows: number;
    cashIn: string;
    cashOut: string;
    netCashMovement: string;
    attributedRows: number;
    unassignedRows: number;
    orgLevelRows: number;
    ambiguousRows: number;
    unresolvedCashRows: number;
    chainConflictRows: number;
    unknownFeeRows: number;
    unknownRebateRows: number;
    actualG1FeesUsd: string;
    estimatedG1RebatesUsd: string;
  };
  rows: ActivityRow[];
  nextCursor: string | null;
  sourceCutoff: string;
}

interface SettlementRow {
  marketId: string;
  side: string;
  trader: string;
  netCount: string;
  tradingRealizedUsd: string;
  settlementPnlUsd: string;
  settledAt: string;
  status: 'open' | 'settled';
  title: string;
  realizedFeesUsd: string;
  totalRealizedNetUsd: string;
  evidenceStatus: string;
  userId: string;
}

interface SettlementSnapshot {
  mode: 'settlement-comparison';
  summary: {
    rows: number;
    tradingRealizedUsd: string;
    settlementPnlUsd: string;
    realizedFeesUsd: string;
    totalRealizedNetUsd: string;
    missingTitles: number;
    unassignedRows: number;
    incompleteEvidenceRows: number;
  };
  rows: SettlementRow[];
  nextCursor: string | null;
}

interface HistoricalComparisonProps {
  orgId: string;
  role: string | undefined;
  wallet: string;
  wallets: string[];
  onWalletChange: (wallet: string) => void;
}

const PRESETS = [
  { label: 'All', days: null },
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '365D', days: 365 },
] as const;

const TABLE_PAGE_SIZE = 50;

function inputDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function rangeForDays(days: number | null): { from: string; to: string } {
  if (days == null) return { from: '', to: '' };
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return { from: inputDate(from), to: inputDate(to) };
}

function money(value: string): string {
  if (value.trim() === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(parsed);
}

function compact(value: string): string {
  if (value.trim() === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value || '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(parsed);
}

function cashSignBasisLabel(basis: CashSignBasis): string {
  if (basis === 'comparison_fixture') return 'fixture';
  if (basis === 'public_chain_fixture') return 'public chain';
  if (basis === 'chain_exact') return 'exact chain';
  return 'unresolved';
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

class ComparisonRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ComparisonRequestError';
  }
}

async function responseError(response: Response): Promise<ComparisonRequestError> {
  const payload = await response.json().catch(() => null) as {
    error?: unknown;
    code?: unknown;
  } | null;
  return new ComparisonRequestError(
    typeof payload?.error === 'string' ? payload.error : `Request failed (${response.status})`,
    response.status,
    typeof payload?.code === 'string' ? payload.code : undefined,
  );
}

function downloadResponseBlob(response: Response, fallback: string): Promise<void> {
  return response.blob().then((blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filenameFromDisposition(response.headers.get('Content-Disposition')) ?? fallback;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
}

function SummaryValue({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border-border bg-background/40 rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 text-base font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-muted-foreground mt-0.5 text-[10px]">{hint}</div>}
    </div>
  );
}

function SortHeader({
  label,
  field,
  active,
  direction,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  active: string;
  direction: SortDirection;
  onSort: (field: string) => void;
  align?: 'left' | 'right';
}) {
  return (
    <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide"
      >
        {label}
        {active === field && (direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </button>
    </th>
  );
}

function TraderPicker({
  traders,
  selected,
  onChange,
  disabled,
}: {
  traders: TraderOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const traderById = useMemo(() => new Map(traders.map((trader) => [trader.id, trader])), [traders]);
  const filtered = traders.filter((trader) => trader.username.toLowerCase().includes(query.toLowerCase()));
  const toggle = (id: string) => onChange(
    selectedSet.has(id) ? selected.filter((value) => value !== id) : [...selected, id],
  );

  return (
    <div className="relative min-w-64 flex-1">
      <label htmlFor="cash-ledger-traders" className="text-muted-foreground mb-1 block text-[11px] font-medium">
        Traders
      </label>
      <div className="bg-background border-border flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1">
        {selected.map((id) => {
          const trader = traderById.get(id);
          if (!trader) return null;
          return (
            <span key={id} className="bg-blue-500/15 text-blue-400 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]">
              {trader.username}
              <button type="button" onClick={() => toggle(id)} title={`Remove ${trader.username}`}><X size={9} /></button>
            </span>
          );
        })}
        <Search size={12} className="text-muted-foreground" />
        <input
          id="cash-ledger-traders"
          value={query}
          disabled={disabled}
          placeholder={selected.length ? 'Add trader' : 'All traders'}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          className="min-w-24 flex-1 bg-transparent py-1 text-xs outline-none"
        />
      </div>
      {open && (
        <div className="bg-card border-border absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border shadow-xl">
          {filtered.length === 0 ? (
            <div className="text-muted-foreground px-3 py-2 text-xs">No matching traders</div>
          ) : filtered.map((trader) => (
            <button
              key={trader.id}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => toggle(trader.id)}
              className="hover:bg-muted flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs"
            >
              <span>{trader.username}</span>
              <span className="text-muted-foreground ml-auto text-[10px] uppercase">{trader.role}</span>
              {selectedSet.has(trader.id) && <Check size={12} className="text-blue-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function HistoricalComparison({
  orgId,
  role,
  wallet,
  wallets,
  onWalletChange,
}: HistoricalComparisonProps) {
  const initialRange = useMemo(() => rangeForDays(30), []);
  const [dataset, setDataset] = useState<Dataset>('activity');
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [traders, setTraders] = useState<TraderOption[]>([]);
  const [selectedTraders, setSelectedTraders] = useState<string[]>([]);
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [activity, setActivity] = useState<ActivitySnapshot | null>(null);
  const [settlements, setSettlements] = useState<SettlementSnapshot | null>(null);
  const [sortBy, setSortBy] = useState('ts_utc');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failedCursor, setFailedCursor] = useState<string | null>(null);
  const [requiresReload, setRequiresReload] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const previousOrgId = useRef(orgId);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const commonParams = useCallback((cursor?: string | null) => {
    const params = new URLSearchParams();
    if (orgId) params.set('orgId', orgId);
    if (wallet) params.set('wallet', wallet);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    for (const traderId of selectedTraders) params.append('traderId', traderId);
    if (includeUnassigned && selectedTraders.length > 0) params.set('includeUnassigned', 'true');
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    params.set('limit', String(TABLE_PAGE_SIZE));
    if (cursor) params.set('cursor', cursor);
    return params;
  }, [from, includeUnassigned, orgId, selectedTraders, sortBy, sortDirection, to, wallet]);

  useEffect(() => {
    if (role === 'admin' && !orgId) {
      setTraders([]);
      return;
    }
    const params = new URLSearchParams();
    if (orgId) params.set('orgId', orgId);
    const suffix = params.size ? `?${params}` : '';
    void apiFetch(`/api/manager/traders${suffix}`)
      .then(async (response) => {
        if (!response.ok) throw await responseError(response);
        return response.json() as Promise<{ traders?: TraderOption[] }>;
      })
      .then((payload) => setTraders(payload.traders ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load traders'));
  }, [orgId, role]);

  useEffect(() => {
    if (previousOrgId.current === orgId) return;
    previousOrgId.current = orgId;
    setSelectedTraders([]);
    setActivity(null);
    setSettlements(null);
  }, [orgId]);

  const load = useCallback(async (cursor?: string | null, append = false) => {
    if ((role === 'admin' && !orgId) || (dataset === 'activity' && !wallet)) return;
    const gen = append ? generation.current : ++generation.current;
    if (!append && tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadingMore(false);
      setFailedCursor(null);
      setRequiresReload(false);
    }
    setError(null);
    try {
      const params = commonParams(cursor);
      const endpoint = dataset === 'activity'
        ? '/api/manager/org/polymarket-cash-ledger/activity'
        : '/api/manager/org/polymarket-cash-ledger/settlements';
      const response = await apiFetch(`${endpoint}?${params}`);
      if (!response.ok) throw await responseError(response);
      if (generation.current !== gen) return;
      if (dataset === 'activity') {
        const payload = await response.json() as ActivitySnapshot;
        setActivity((current) => append && current
          ? { ...payload, rows: [...current.rows, ...payload.rows] }
          : payload);
      } else {
        const payload = await response.json() as SettlementSnapshot;
        setSettlements((current) => append && current
          ? { ...payload, rows: [...current.rows, ...payload.rows] }
          : payload);
      }
      if (append) setFailedCursor(null);
    } catch (err) {
      if (generation.current === gen) {
        setError(err instanceof Error ? err.message : 'Could not load comparison');
        if (
          append
          && cursor
          && err instanceof ComparisonRequestError
          && (
            err.code === 'ACTIVITY_SOURCE_DRIFT'
            || err.code === 'INVALID_PREVIEW_CURSOR'
          )
        ) {
          setFailedCursor(null);
          setRequiresReload(true);
        } else if (append && cursor) {
          setFailedCursor(cursor);
        }
      }
    } finally {
      if (generation.current === gen) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [commonParams, dataset, orgId, role, wallet]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSort = (field: string) => {
    if (sortBy === field) setSortDirection((value) => value === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDirection('asc'); }
  };

  const download = async () => {
    if (dataset === 'activity' && !wallet) return;
    setExporting(true);
    setError(null);
    try {
      const params = commonParams();
      params.delete('cursor');
      params.delete('limit');
      const endpoint = dataset === 'activity'
        ? '/api/manager/org/polymarket-cash-ledger/activity.csv'
        : '/api/manager/org/settlements-titled.csv';
      const response = await apiFetch(`${endpoint}?${params}`);
      if (!response.ok) throw await responseError(response);
      await downloadResponseBlob(response, dataset === 'activity' ? 'activity-cash-ledger.csv' : 'settlements-titled.csv');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const setPreset = (days: number | null) => {
    const range = rangeForDays(days);
    setFrom(range.from);
    setTo(range.to);
  };

  const nextCursor = dataset === 'activity' ? activity?.nextCursor : settlements?.nextCursor;

  useEffect(() => {
    const root = tableScrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (
      !root
      || !sentinel
      || !nextCursor
      || loading
      || loadingMore
      || requiresReload
      || failedCursor === nextCursor
    ) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      void load(nextCursor, true);
    }, {
      root,
      rootMargin: '0px 0px 160px 0px',
      threshold: 0.01,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [failedCursor, load, loading, loadingMore, nextCursor, requiresReload]);

  const paginationRow = (
    <tfoot data-testid="cash-ledger-pagination-row">
      <tr className="border-border h-10 border-t">
        <td colSpan={11} className="p-0">
          <div
            ref={nextCursor ? loadMoreSentinelRef : undefined}
            className="bg-muted/25 text-muted-foreground flex h-10 items-center justify-center gap-2 text-[11px]"
            aria-live="polite"
          >
            {requiresReload && nextCursor ? (
              <button type="button" onClick={() => void load(null, false)} className="text-blue-400 hover:text-blue-300">
                Source changed; reload from first row
              </button>
            ) : failedCursor === nextCursor && nextCursor ? (
              <button type="button" onClick={() => void load(nextCursor, true)} className="text-blue-400 hover:text-blue-300">
                Retry loading more rows
              </button>
            ) : nextCursor ? (
              <>
                <Loader2 size={13} className={`animate-spin ${loadingMore ? 'text-blue-400' : 'opacity-60'}`} />
                {loadingMore ? 'Loading more rows…' : 'More rows load at the bottom'}
              </>
            ) : (
              <><Check size={13} className="text-emerald-400" /> All rows loaded</>
            )}
          </div>
        </td>
      </tr>
    </tfoot>
  );

  return (
    <section className="bg-card border-border overflow-visible rounded-lg border">
      <div className="border-border border-b px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Historical comparison export</h2>
            <p className="text-muted-foreground mt-0.5 text-[11px]">
              Familiar source columns first, followed by G1 attribution and evidence. This is read-only and does not alter the books.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void download()}
            disabled={exporting || loading || (dataset === 'activity' && !wallet)}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Download {dataset === 'activity' ? 'activity CSV' : 'settlements CSV'}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="min-w-52 flex-1">
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Authorized funder wallet</span>
            <select
              value={wallet}
              onChange={(event) => onWalletChange(event.target.value)}
              className="bg-background border-border w-full rounded-md border px-2.5 py-2 font-mono text-xs"
            >
              {wallets.length !== 1 && <option value="">Select wallet…</option>}
              {wallets.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="min-w-48">
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Dataset</span>
            <select
              value={dataset}
              onChange={(event) => {
                const next = event.target.value as Dataset;
                setDataset(next);
                setSortBy(next === 'activity' ? 'ts_utc' : 'settled_at');
                setSortDirection('desc');
              }}
              className="bg-background border-border w-full rounded-md border px-2.5 py-2 text-xs"
            >
              <option value="activity">Activity / cash ledger</option>
              <option value="settlements">Settlements titled</option>
            </select>
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">From (UTC)</span>
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="bg-background border-border rounded-md border px-2.5 py-2 text-xs" />
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">To (UTC)</span>
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} className="bg-background border-border rounded-md border px-2.5 py-2 text-xs" />
          </label>
          <div className="flex gap-1 pb-0.5">
            {PRESETS.map((preset) => (
              <button key={preset.label} type="button" onClick={() => setPreset(preset.days)} className="bg-secondary text-muted-foreground hover:text-foreground rounded px-2 py-1.5 text-[10px] font-semibold">
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <TraderPicker traders={traders} selected={selectedTraders} onChange={setSelectedTraders} disabled={loading} />
          {dataset === 'activity' && (
            <label className="text-muted-foreground flex items-center gap-2 pb-2 text-xs">
              <input
                type="checkbox"
                checked={includeUnassigned}
                disabled={selectedTraders.length === 0}
                onChange={(event) => setIncludeUnassigned(event.target.checked)}
              />
              Include unassigned / org-level rows
            </label>
          )}
        </div>
      </div>

      {error && <div className="border-red-500/30 bg-red-500/10 m-4 rounded-md border px-3 py-2 text-xs text-red-300">{error}</div>}

      {loading ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm"><Loader2 size={16} className="animate-spin" /> Loading comparison…</div>
      ) : dataset === 'activity' && activity ? (
        <>
          <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
            <SummaryValue label="Rows" value={activity.summary.returnedRows.toLocaleString()} hint={`${activity.summary.excludedRows.toLocaleString()} excluded`} />
            <SummaryValue label="Cash in" value={money(activity.summary.cashIn)} />
            <SummaryValue label="Cash out" value={money(activity.summary.cashOut)} />
            <SummaryValue label="Net cash" value={money(activity.summary.netCashMovement)} />
            <SummaryValue label="Attributed" value={activity.summary.attributedRows.toLocaleString()} hint={`${activity.summary.unassignedRows + activity.summary.orgLevelRows + activity.summary.ambiguousRows} unresolved/org-level`} />
            <SummaryValue
              label="Cash sign gaps"
              value={activity.summary.unresolvedCashRows.toLocaleString()}
              hint={activity.summary.chainConflictRows > 0
                ? `${activity.summary.chainConflictRows.toLocaleString()} exact chain ${activity.summary.chainConflictRows === 1 ? 'conflict' : 'conflicts'}; excluded`
                : 'Excluded from cash totals'}
            />
            <SummaryValue
              label="Actual G1 fees"
              value={money(activity.summary.actualG1FeesUsd)}
              hint={activity.summary.unknownFeeRows > 0
                ? `Known evidence only; ${activity.summary.unknownFeeRows.toLocaleString()} unknown`
                : 'Matched G1 fills only'}
            />
            <SummaryValue
              label="Est. rebates"
              value={money(activity.summary.estimatedG1RebatesUsd)}
              hint={activity.summary.unknownRebateRows > 0
                ? `Known evidence only; ${activity.summary.unknownRebateRows.toLocaleString()} unknown`
                : 'Never booked as cash'}
            />
          </div>
          {activity.summary.unresolvedCashRows > 0 && (
            <div className="border-amber-500/30 bg-amber-500/10 mx-4 mb-3 rounded-md border px-3 py-2 text-[11px] text-amber-200">
              Net cash excludes {activity.summary.unresolvedCashRows.toLocaleString()} source {activity.summary.unresolvedCashRows === 1 ? 'row' : 'rows'} whose direction is not proven by the activity contract or an exact chain transfer.
              {activity.summary.chainConflictRows > 0 && ` ${activity.summary.chainConflictRows.toLocaleString()} ${activity.summary.chainConflictRows === 1 ? 'row has' : 'rows have'} an exact chain-direction conflict.`}
            </div>
          )}
          {(activity.summary.unknownFeeRows > 0 || activity.summary.unknownRebateRows > 0) && (
            <div className="border-amber-500/30 bg-amber-500/10 mx-4 mb-3 rounded-md border px-3 py-2 text-[11px] text-amber-200">
              Fee totals include known evidence only. Missing fee or rebate evidence is not treated as zero:
              {activity.summary.unknownFeeRows > 0 && (
                <> {activity.summary.unknownFeeRows.toLocaleString()} fee {activity.summary.unknownFeeRows === 1 ? 'row' : 'rows'}</>
              )}
              {activity.summary.unknownFeeRows > 0 && activity.summary.unknownRebateRows > 0 && ' and'}
              {activity.summary.unknownRebateRows > 0 && (
                <> {activity.summary.unknownRebateRows.toLocaleString()} rebate {activity.summary.unknownRebateRows === 1 ? 'row' : 'rows'}</>
              )}
              {activity.summary.unknownFeeRows + activity.summary.unknownRebateRows === 1 ? ' is' : ' are'} unknown.
            </div>
          )}
          {selectedTraders.length > 0 && (
            <div className="border-amber-500/30 bg-amber-500/10 mx-4 mb-3 rounded-md border px-3 py-2 text-[11px] text-amber-200">
              This trader slice is not a wallet balance. Other-trader and unassigned cash may be excluded.
            </div>
          )}
          <CashLedgerTableViewport
            ref={tableScrollRef}
            testId="cash-ledger-history-table-scroll"
          >
            <table className="w-full min-w-[1420px] text-xs">
              <thead className={CASH_LEDGER_TABLE_HEADER_CLASS}>
                <tr>
                  <SortHeader label="Time" field="ts_utc" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <SortHeader label="Type" field="type" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <SortHeader label="Market / outcome" field="title" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <SortHeader label="Shares" field="shares" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <SortHeader label="Price" field="price" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <SortHeader label="USDC" field="usdc" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <SortHeader label="Cash delta" field="cash_delta" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <SortHeader label="Trader" field="trader" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <th className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold uppercase">Role / fee</th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold uppercase">Evidence</th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold uppercase">Transaction</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {activity.rows.length === 0 ? (
                  <tr><td colSpan={11} className="text-muted-foreground px-4 py-12 text-center">No activity in this slice.</td></tr>
                ) : activity.rows.map((row) => (
                  <tr key={row.sourceId} className={`${CASH_LEDGER_TABLE_ROW_CLASS} hover:bg-muted/20`}>
                    <td className="text-muted-foreground whitespace-nowrap px-3 py-2">{new Date(row.tsUtc).toLocaleString()}</td>
                    <td className="px-3 py-2"><span className="bg-muted rounded px-1.5 py-0.5 text-[10px] font-semibold">{row.type}</span>{row.side && <span className="text-muted-foreground ml-1 text-[10px]">{row.side}</span>}</td>
                    <td className="max-w-sm px-3 py-2"><div className="truncate">{row.title || 'Untitled activity'}</div><div className="text-muted-foreground truncate text-[10px]">{row.outcome || row.conditionId}</div></td>
                    <td className="px-3 py-2 text-right tabular-nums">{compact(row.shares)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{compact(row.price)}</td>
                    <td data-money-cell="true" className="h-[65px] p-0 text-right"><DecimalAlignedMoney value={row.usdc} /></td>
                    <td data-money-cell="true" className={`h-[65px] p-0 text-right font-semibold ${row.cashDelta === '' ? 'text-muted-foreground' : row.cashDelta.startsWith('-') ? 'text-red-400' : 'text-emerald-400'}`}><DecimalAlignedMoney value={row.cashDelta} /></td>
                    <td className="px-3 py-2"><div>{row.trader || 'Unassigned / org-level'}</div><div className="text-muted-foreground text-[10px]">{row.attributionStatus}</div></td>
                    <td className="px-3 py-2"><div>{row.g1ExecutionRole || '—'}</div><div className="text-muted-foreground text-[10px]">{row.g1FeeUsdActual ? `${money(row.g1FeeUsdActual)} fee` : 'No matched fee'}</div></td>
                    <td className="px-3 py-2"><div title={row.cashSignReason}>{row.evidenceStatus}</div><div className="text-muted-foreground whitespace-nowrap text-[10px]">cash: {cashSignBasisLabel(row.cashSignBasis)} · chain: {row.chainMatchStatus}</div></td>
                    <td className="px-3 py-2">{row.tx ? <a href={`https://polygonscan.com/tx/${row.tx}`} target="_blank" rel="noreferrer" className="text-blue-400 inline-flex items-center gap-1 font-mono">{row.tx.slice(0, 8)}… <ExternalLink size={10} /></a> : '—'}</td>
                  </tr>
                ))}
              </tbody>
              {paginationRow}
            </table>
          </CashLedgerTableViewport>
        </>
      ) : dataset === 'settlements' && settlements ? (
        <>
          <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <SummaryValue label="Rows" value={settlements.summary.rows.toLocaleString()} />
            <SummaryValue label="Trading realized" value={money(settlements.summary.tradingRealizedUsd)} />
            <SummaryValue label="Settlement P&L" value={money(settlements.summary.settlementPnlUsd)} />
            <SummaryValue label="Actual fees" value={money(settlements.summary.realizedFeesUsd)} hint="Known evidence only" />
            <SummaryValue label="Total net" value={money(settlements.summary.totalRealizedNetUsd)} hint={settlements.summary.incompleteEvidenceRows > 0 ? 'Incomplete rows excluded' : undefined} />
            <SummaryValue label="Missing titles" value={settlements.summary.missingTitles.toLocaleString()} />
            <SummaryValue label="Evidence gaps" value={settlements.summary.incompleteEvidenceRows.toLocaleString()} />
          </div>
          <CashLedgerTableViewport
            ref={tableScrollRef}
            testId="cash-ledger-history-table-scroll"
          >
            <table className="w-full min-w-[1240px] text-xs">
              <thead className={CASH_LEDGER_TABLE_HEADER_CLASS}>
                <tr>
                  <SortHeader label="Settled at" field="settled_at" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <SortHeader label="Status" field="status" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <SortHeader label="Market" field="title" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <th className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold uppercase">Side</th>
                  <SortHeader label="Trader" field="trader" active={sortBy} direction={sortDirection} onSort={onSort} />
                  <th className="text-muted-foreground px-3 py-2 text-right text-[10px] font-semibold uppercase">Count</th>
                  <th className="text-muted-foreground px-3 py-2 text-right text-[10px] font-semibold uppercase">Trading realized</th>
                  <SortHeader label="Settlement P&L" field="settlement_pnl_usd" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <th className="text-muted-foreground px-3 py-2 text-right text-[10px] font-semibold uppercase">Fees</th>
                  <SortHeader label="Total net" field="total_realized_net_usd" active={sortBy} direction={sortDirection} onSort={onSort} align="right" />
                  <th className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold uppercase">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {settlements.rows.length === 0 ? (
                  <tr><td colSpan={11} className="text-muted-foreground px-4 py-12 text-center">No settlements in this slice.</td></tr>
                ) : settlements.rows.map((row) => (
                  <tr key={`${row.userId}:${row.marketId}:${row.side}`} className={`${CASH_LEDGER_TABLE_ROW_CLASS} hover:bg-muted/20`}>
                    <td className="text-muted-foreground whitespace-nowrap px-3 py-2">{row.settledAt ? new Date(row.settledAt).toLocaleString() : 'Open'}</td>
                    <td className="px-3 py-2">{row.status}</td>
                    <td className="max-w-sm px-3 py-2"><div className="truncate">{row.title || 'Untitled market'}</div><div className="text-muted-foreground truncate font-mono text-[10px]">{row.marketId}</div></td>
                    <td className="px-3 py-2 uppercase">{row.side}</td>
                    <td className="px-3 py-2">{row.trader}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{compact(row.netCount)}</td>
                    <td data-money-cell="true" className="h-[65px] p-0 text-right"><DecimalAlignedMoney value={row.tradingRealizedUsd} minimumFractionDigits={4} maximumFractionDigits={4} /></td>
                    <td data-money-cell="true" className="h-[65px] p-0 text-right"><DecimalAlignedMoney value={row.settlementPnlUsd} minimumFractionDigits={4} maximumFractionDigits={4} /></td>
                    <td data-money-cell="true" className="h-[65px] p-0 text-right"><DecimalAlignedMoney value={row.realizedFeesUsd} minimumFractionDigits={4} maximumFractionDigits={4} /></td>
                    <td data-money-cell="true" className="h-[65px] p-0 text-right font-semibold"><DecimalAlignedMoney value={row.totalRealizedNetUsd} minimumFractionDigits={4} maximumFractionDigits={4} /></td>
                    <td className="px-3 py-2">{row.evidenceStatus}</td>
                  </tr>
                ))}
              </tbody>
              {paginationRow}
            </table>
          </CashLedgerTableViewport>
        </>
      ) : (
        <div className="text-muted-foreground px-4 py-12 text-center text-sm">Choose an authorized wallet to load history.</div>
      )}
    </section>
  );
}
